import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles, ClassNameMap, createStyles } from '@mui/styles';
import { Fragment, ReactElement } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CollapseItem from './collapse-item';
import { menuItems } from 'shell/constants/menu-items';
import { noop } from 'shell/utils/noop';
import { useMenuItemsAuthorization } from './use-menu-item-authorization';

interface StyleProps {
  width: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      color: theme.palette.common.white,
    },
    drawerPaper: (props: StyleProps) => ({
      width: `${props.width}px`,
      border: 'none !important',
      backgroundColor: `${theme.palette.common.white} !important`,
      // backgroundColor: `${theme.palette.cdmBrand.main} !important`,
      color: `${theme.palette.common.white} !important`,
    }),
    imageHeader: {
      width: '3rem',
      // paddingTop: theme.spacing(1),
      // paddingBottom: theme.spacing(1),
      // paddingRight: theme.spacing(2),
    },
    drawerHeader: {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      padding: theme.spacing(0, 1),
      backgroundColor: `${theme.palette.cdmBrand.main} !important`,
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    colorItems: {
      color: `#555 !important`,
    },
  })
);

interface SideMenuProps {
  visible: boolean;
  onClose: () => void;
  onItemClick?: () => void;
  width: number;
}

const SideMenu = ({ visible, onClose, onItemClick = noop, width }: SideMenuProps): ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const classes = useStyles({ width }) as ClassNameMap<string>;

  const activeItems = useMenuItemsAuthorization(menuItems);

  return (
    <Fragment>
      <Drawer
        anchor="left"
        variant={fullScreen ? 'temporary' : 'persistent'}
        open={visible}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
      >
        <div
          className={classNames(
            classes.drawerHeader,
            'pmi-d-flex pmi-justify-content-between pmi-align-items-center'
          )}
        >
          <div className={classes.imageHeader}>
            <img src="/images/logo-cdm.png" alt="Logo Comune Milano" width="100%" />
          </div>
          <IconButton onClick={onClose} color="inherit">
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <div className={classNames(classes.list)} role="presentation">
          <List>
            {activeItems.map((item) => {
              const IconItem = item.icon || (() => <></>);

              return !Array.isArray(item.subItems) || !item.subItems.length ? (
                <Link key={item.link} to={item.link || ''} className="pmi-anchor">
                  <ListItem
                    button
                    key={item.label}
                    onClick={() => {
                      onItemClick();
                    }}
                  >
                    <ListItemIcon className={classes.colorItems}>
                      <IconItem color="inherit" />
                    </ListItemIcon>
                    <ListItemText className={classes.colorItems} primary={item.label} />
                  </ListItem>
                </Link>
              ) : (
                <CollapseItem
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  link={item.link}
                  subItems={item.subItems}
                  onSubItemClick={onItemClick}
                  nestedItems={item.nestedItems}
                />
              );
            })}
          </List>
        </div>
      </Drawer>
    </Fragment>
  );
};

SideMenu.displayName = 'SideMenu';

export default SideMenu;
